// @flow
import type { Dispatch } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { isFetching, isFetched } from './selectors';
import { isMultiLanguage } from '../../helpers';
import { getActiveLanguage } from '../activeLanguage';
import fetchData, { type Arguments } from '../../helpers/fetchData';
import type { OpeningHours } from '../../types';

import {
  GET_OPENING_HOURS,
  GET_OPENING_HOURS_SUCCESS,
  GET_OPENING_HOURS_FAILED,
} from './constants';
import type { StartFetchOpeningHours, ReceivedOpeningHours, FailedOpeningHours } from './types/actions';

export const startFetchOpeningHours = (): StartFetchOpeningHours => ({
  type: GET_OPENING_HOURS,
});

export const receivedOpeningHours: ReceivedOpeningHours = data => ({
  type: GET_OPENING_HOURS_SUCCESS,
  payload: data,
});

export const failedOpeningHours: FailedOpeningHours = err => ({
  type: GET_OPENING_HOURS_FAILED,
  payload: err,
});

type Fetcher = (_: Arguments) => Promise<{ json: OpeningHours }>;

const fetchOpeningHours = (
  fetcher: Fetcher = fetchData,
  forced: boolean = false,
) => async (dispatch: Dispatch<*>, getState: Function) => {
  const state = getState();
  // dont double fetch it, every withOpeningHours will call this fetchter
  if (isFetching(state)) {
    return true;
  }

  // we dont need to load it when we have it, except when we force a refetch
  if (isFetched(state) && !forced) {
    return true;
  }

  dispatch(startFetchOpeningHours());
  const language = isMultiLanguage() ? getActiveLanguage(state) : null;

  let openingHours;
  try {
    const { json } = await fetcher({
      endpoint: '/api/isopen',
      language,
    });
    openingHours = json;
  } catch (err) {
    return dispatch(failedOpeningHours(err));
  }
  return dispatch(receivedOpeningHours(openingHours));
};

let timerRegisteredComponents = 0;
let timer;

export const stopOpeningHoursPolling = () => () => {
  timerRegisteredComponents -= 1;

  if (timerRegisteredComponents <= 0) {
    clearInterval(timer);
    timer = undefined;
    timerRegisteredComponents = 0;
  }
};

export const startOpeningHoursPolling = () => (dispatch: ThunkAction<*>) => {
  timerRegisteredComponents += 1;

  if (timer !== undefined) {
    return;
  }

  // start polling
  timer = setInterval(
    () => dispatch(fetchOpeningHours(undefined, true)),
    60000,
  );
};

export default fetchOpeningHours;
